<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import AppLayout from '@/layouts/AppLayout.vue'
import { getPlanP16Label, getServicePlan, savePlanP16Label, saveServiceTypeP16Default } from '@/services/plans'
import type { ServicePlanDetail } from '@/types/plans'
import type { P16LabelChannel } from '@/types/p16'

const route = useRoute()
const plan = ref<ServicePlanDetail>()
const channels = ref<P16LabelChannel[]>([])
const scale = ref(100)
const scaleInput = ref('100')
const loading = ref(true)
const saving = ref(false)
const savingDefault = ref(false)
const error = ref('')
const savedAt = ref<string>()
const savedBy = ref('')
const savedSignature = ref('')
const labelSource = ref<'plan' | 'service-type' | 'blank'>('blank')
const defaultNotice = ref('')
const activeColorPicker = ref<number>()
const colors = [
  { id:1, name:'Gray blue', hex:'#3e63cc' }, { id:2, name:'Medium blue', hex:'#0180ff' }, { id:3, name:'Dark blue', hex:'#5a33ff' }, { id:4, name:'Turquoise', hex:'#00ced1' }, { id:5, name:'Green', hex:'#01b23e' }, { id:6, name:'Olive green', hex:'#96cc00' }, { id:7, name:'Yellow', hex:'#f2dd00' }, { id:8, name:'Orange', hex:'#c06a1f' }, { id:9, name:'Red', hex:'#701020' }, { id:10, name:'Coral', hex:'#ff7a7a' }, { id:11, name:'Pink', hex:'#ff33f6' }, { id:12, name:'Mauve', hex:'#a533ff' }, { id:13, name:'Amber', hex:'#ffb81a' }, { id:14, name:'Sky blue', hex:'#25c3ff' }, { id:15, name:'Vermilion', hex:'#ff5a30' }, { id:16, name:'Mint', hex:'#33e6a5' }, { id:17, name:'Gray', hex:'#707070' }, { id:18, name:'White', hex:'#e0e0e0' },
]
const colorFor = (id: number) => colors.find(color => color.id === id) || colors[16]
const labelSignature = () => JSON.stringify({ scale:scale.value, channels:channels.value })
const dirty = computed(() => Boolean(channels.value.length) && labelSignature() !== savedSignature.value)
const planDate = computed(() => plan.value?.dates || plan.value?.startsAt || 'Service date pending')
const planName = computed(() => plan.value?.planTitle || plan.value?.title || 'Service plan')
const savedDescription = computed(() => {
  const saved = savedAt.value ? `Saved ${new Intl.DateTimeFormat(undefined, { dateStyle:'medium', timeStyle:'short' }).format(new Date(savedAt.value))}${savedBy.value ? ` by ${savedBy.value}` : ''}` : 'Not yet saved'
  return labelSource.value === 'service-type' ? `Using ${plan.value?.serviceTypeName || 'service-type'} default · ${saved}` : saved
})
const isStereoFollower = (index: number) => index > 0 && channels.value[index - 1]?.stereo === true
const channelGroups = computed(() => Array.from({ length:4 }, (_, groupIndex) => channels.value.slice(groupIndex * 4, groupIndex * 4 + 4)))
const printedChannelX = (groupIndex: number, channelIndex: number) => groupIndex * 53 + channelIndex * 12.5
const printedChannelWidth = (channel: P16LabelChannel) => channel.stereo ? 25 : 12.5
const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')
const bandPositionTags = ['Vocals', 'Acoustic Guitar', 'Electric Guitar', 'Bass Guitar', 'Bass', 'Drums', 'Percussion', 'Keys', 'Keyboard', 'Piano', 'Worship Leader']
const bandMembers = computed(() => plan.value?.teamMembers.filter(member => member.positions.some(position => bandPositionTags.map(normalize).includes(normalize(position)))) || [])

function setStereo(index: number, checked: boolean) {
  const start = index % 2 === 0 ? index : index - 1
  const channel = channels.value[start]
  if (!channel || start === channels.value.length - 1) return
  channel.stereo = checked
  if (checked) channels.value[start + 1].stereo = false
}

function resetLabel() {
  if (!window.confirm('Reset all P16 channel names, colors, stereo links, and print scale for this plan?')) return
  scale.value = 100
  channels.value = Array.from({ length:16 }, (_, index) => ({ number:index + 1, name:'', subtitle:'', color:17, stereo:false }))
  activeColorPicker.value = undefined
  error.value = ''
}

function selectColor(channel: P16LabelChannel, color: number) {
  channel.color = color
  activeColorPicker.value = undefined
}

function commitScale() {
  const enteredScale = Number(scaleInput.value.trim())
  scale.value = Number.isFinite(enteredScale) ? Math.min(150, Math.max(50, enteredScale)) : 100
  scaleInput.value = String(scale.value)
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const planId = String(route.params.planId)
    const [servicePlan, label] = await Promise.all([getServicePlan(planId), getPlanP16Label(planId)])
    plan.value = servicePlan
    scale.value = label.scale
    channels.value = label.channels.map(channel => ({ ...channel }))
    savedAt.value = label.updatedAt
    savedBy.value = label.updatedByName || label.updatedByEmail || ''
    labelSource.value = label.source || 'plan'
    savedSignature.value = labelSignature()
  } catch { error.value = 'The P16 label workspace could not be loaded.' }
  finally { loading.value = false }
}

async function save() {
  if (!plan.value || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const label = await savePlanP16Label(plan.value.id, scale.value, channels.value)
    scale.value = label.scale
    channels.value = label.channels.map(channel => ({ ...channel }))
    savedAt.value = label.updatedAt
    savedBy.value = label.updatedByName || label.updatedByEmail || ''
    labelSource.value = 'plan'
    defaultNotice.value = ''
    savedSignature.value = labelSignature()
  } catch { error.value = 'The P16 label could not be saved. Please try again.' }
  finally { saving.value = false }
}

async function saveAsDefault() {
  if (!plan.value || saving.value || savingDefault.value) return
  commitScale()
  savingDefault.value = true
  error.value = ''
  try {
    const label = await saveServiceTypeP16Default(plan.value.id, scale.value, channels.value)
    defaultNotice.value = `${label.serviceTypeName || plan.value.serviceTypeName} default saved for future plans.`
  } catch { error.value = 'The service-type default could not be saved. Please try again.' }
  finally { savingDefault.value = false }
}

async function printLabel() {
  if (dirty.value) await save()
  if (!error.value) window.print()
}

watch(scale, value => { if (!Number.isFinite(value)) scale.value = 100; else scaleInput.value = String(Math.min(150, Math.max(50, value))) })
onMounted(load)
</script>

<template>
  <AppLayout>
    <RouterLink class="back-link p16-no-print" :to="`/plans/${route.params.planId}`">← Back to plan</RouterLink>
    <p v-if="loading" class="loading-state"><span class="spinner" aria-hidden="true"></span>Loading P16 label workspace…</p>
    <p v-else-if="error && !plan" class="error">{{ error }}</p>
    <template v-else-if="plan">
      <header class="page-heading p16-editor-header p16-no-print"><p class="eyebrow">PLAN-LEVEL P16 LABEL</p><h1>Personal monitor strip</h1><p>{{ planName }} · {{ planDate }}</p></header>

      <section class="panel p16-controls p16-no-print">
        <div><p class="eyebrow">PRINT CALIBRATION</p><h2>Print at the scale shown on the label</h2><p class="muted">Start at 100%. The printed 100 mm ruler lets you confirm the output with a real ruler, then fine-tune only this value if your printer changes the size. In the print dialog, select Color and enable background graphics.</p></div>
        <label>Label scale<input v-model="scaleInput" type="text" inputmode="decimal" autocomplete="off" aria-label="Label scale percentage" @blur="commitScale" @keyup.enter="($event.target as HTMLInputElement).blur()"><span>%</span></label>
        <div class="p16-control-actions"><small :class="{ notice:savedAt || defaultNotice }">{{ saving ? 'Saving to plan…' : savingDefault ? 'Saving service-type default…' : defaultNotice || savedDescription }}</small><div><button class="button" :disabled="saving || savingDefault" @click="resetLabel">Reset</button><button class="button" :disabled="saving || savingDefault" @click="saveAsDefault"><span v-if="savingDefault" class="spinner" aria-hidden="true"></span>{{ savingDefault ? 'Saving default…' : 'Save as default' }}</button><button class="button" :disabled="saving || savingDefault || !dirty" @click="save"><span v-if="saving" class="spinner" aria-hidden="true"></span>{{ saving ? 'Saving…' : 'Save' }}</button><button class="button primary" :disabled="saving || savingDefault" @click="printLabel">Print</button></div></div>
      </section>
      <p v-if="error" class="error p16-no-print">{{ error }}</p>

      <section class="p16-print-sheet" :style="{ '--p16-scale': String(scale / 100) }">
        <div class="p16-calibrated">
          <header class="p16-label-header"><div><b>P16 PERSONAL MONITOR</b><span>{{ planName }}</span></div><div><b>{{ planDate }}</b><span>Label scale: {{ scale.toFixed(1).replace('.0', '') }}%</span></div></header>
          <div class="p16-ruler" aria-label="100 millimeter print ruler"><span v-for="mark in 101" :key="mark" :class="{ major:(mark - 1) % 10 === 0, labelled:(mark - 1) % 10 === 0 }" :data-mm="(mark - 1) % 10 === 0 ? mark - 1 : ''"></span></div>
          <p class="p16-ruler-caption">Calibration ruler — this line should measure exactly 100 mm after printing.</p>
          <div class="p16-print-copies">
            <div v-for="copy in 7" :key="copy" class="p16-label-copy" :aria-hidden="copy > 1">
              <span class="p16-horizontal-crop-guides" aria-hidden="true"></span>
              <svg class="p16-strip" viewBox="0 0 209 20" role="img" aria-label="P16 label preview">
                <defs><pattern id="p16-stereo-stripes" width="3" height="3" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="3" height="3" fill="#fbfbfb"/><rect width="1.5" height="3" fill="#f0f0f0"/></pattern></defs>
                <g v-for="(group, groupIndex) in channelGroups" :key="groupIndex">
                  <template v-for="(channel, index) in group" :key="channel.number">
                    <g v-if="!isStereoFollower(groupIndex * 4 + index)">
                      <rect class="p16-svg-channel-fill" :x="printedChannelX(groupIndex, index)" y="0" :width="printedChannelWidth(channel)" height="20" :fill="channel.stereo ? 'url(#p16-stereo-stripes)' : '#fbfbfb'"/>
                      <rect :x="printedChannelX(groupIndex, index)" y="0" :width="printedChannelWidth(channel)" height="3.5" :fill="colorFor(channel.color).hex"/>
                      <line v-if="index > 0" class="p16-svg-line" :x1="printedChannelX(groupIndex, index)" y1="0" :x2="printedChannelX(groupIndex, index)" y2="20"/>
                      <line class="p16-svg-line p16-svg-soft-line" :x1="printedChannelX(groupIndex, index)" y1="3.5" :x2="printedChannelX(groupIndex, index) + printedChannelWidth(channel)" y2="3.5"/>
                      <line class="p16-svg-line p16-svg-soft-line" :x1="printedChannelX(groupIndex, index)" y1="15.4" :x2="printedChannelX(groupIndex, index) + printedChannelWidth(channel)" y2="15.4"/>
                      <text class="p16-svg-number" font-size="2" :x="printedChannelX(groupIndex, index) + printedChannelWidth(channel) / 2" y="6.1">{{ channel.stereo ? `${channel.number}–${channel.number + 1}` : channel.number }}</text>
                      <text class="p16-svg-name" font-size="3" :x="printedChannelX(groupIndex, index) + printedChannelWidth(channel) / 2" y="11">{{ channel.name || '—' }}</text>
                      <text class="p16-svg-subtitle" font-size="1.75" :x="printedChannelX(groupIndex, index) + printedChannelWidth(channel) / 2" y="17.6">{{ channel.subtitle || (channel.stereo ? 'STEREO' : '') }}</text>
                    </g>
                  </template>
                  <rect class="p16-svg-group-outline" :x="groupIndex * 53" y="0" width="50" height="20"/>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      <div class="p16-editor-workspace p16-no-print">
        <section class="panel p16-editor">
          <div class="section-heading"><div><p class="eyebrow">CHANNEL CONTENT</p><h2>16-channel label editor</h2></div><small>Changes save with this service plan.</small></div>
          <div class="p16-channel-editor">
            <article v-for="(channel, index) in channels" :key="channel.number" :class="{ stereo:channel.stereo, 'stereo-follower':isStereoFollower(index) }">
              <template v-if="!isStereoFollower(index)">
                <div class="p16-editor-number" :style="{ backgroundColor:colorFor(channel.color).hex }">{{ channel.stereo ? `${channel.number}–` : channel.number }}</div>
                <label>Channel name<input v-model="channel.name" maxlength="48" :placeholder="`Channel ${channel.number}`"></label>
                <label>Subtitle<input v-model="channel.subtitle" maxlength="64" placeholder="Optional detail"></label>
                <div class="p16-color-picker"><span>Strip color</span><button type="button" :aria-label="`Choose strip color for channel ${channel.number}`" :title="colorFor(channel.color).name" @click="activeColorPicker = activeColorPicker === channel.number ? undefined : channel.number"><i :style="{ backgroundColor:colorFor(channel.color).hex }"></i></button><div v-if="activeColorPicker === channel.number" class="p16-color-menu"><button v-for="color in colors" :key="color.id" type="button" :class="{ selected:color.id === channel.color }" :aria-label="color.name" :title="color.name" @click="selectColor(channel, color.id)"><i :style="{ backgroundColor:color.hex }"></i></button></div></div>
                <label v-if="channel.number < 16" class="p16-stereo"><input :checked="channel.number % 2 === 1 ? channel.stereo : channels[index - 1].stereo" type="checkbox" @change="setStereo(index, ($event.target as HTMLInputElement).checked)">{{ channel.number % 2 === 1 ? `Link to ${channel.number + 1}` : `Link to ${channel.number - 1}` }}</label>
              </template>
              <template v-else>
                <div class="p16-editor-number" :style="{ backgroundColor:colorFor(channels[index - 1].color).hex }">{{ channel.number }}</div>
                <div class="p16-linked-slot"><b>Linked with channel {{ channels[index - 1].number }}</b><small>Uses channel {{ channels[index - 1].number }} name, subtitle, and color.</small></div>
              </template>
            </article>
          </div>
        </section>
        <details class="p16-band-reference panel"><summary><span><b>Band reference</b><small>Current Planning Center assignments and saved gear notes.</small></span><span>{{ bandMembers.length }} assigned</span></summary><div class="p16-band-members"><RouterLink v-for="member in bandMembers" :key="member.pcoPersonId" :to="`/team/${member.pcoPersonId}`" class="prep-member" :class="{ 'declined-assignment':member.declined }"><b>{{ member.positions.join(' / ') }} — {{ member.name }}</b><small v-if="member.declined" class="declined-status">Declined</small><p v-if="member.gearNotes">{{ member.gearNotes }}</p></RouterLink></div><p v-if="!bandMembers.length" class="muted">No band positions are currently filled.</p></details>
      </div>
    </template>
  </AppLayout>
</template>
